import { Alert, Checkbox, Col, Form, FormInstance, Modal, Row, notification, Select } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { createThirdPartyBkAccount, updateThirdPartyBkAccount, getAvailablePriorities } from 'services/thirdParty';

import Bank from 'pages/ThirdParty/ThirdPartySFS/ThirdPartyCreation/components/Bank';
import ButtonLink from 'components/theme_components/ButtonLink';
import Notifications from 'components/theme_components/Notifications';
import ParListSelect from 'components/ParListSelect';
import StatCard from 'components/theme_components/StatCard';
import StaticCard from 'components/theme_components/StatCard';
import dayjs from 'dayjs';
import { on } from 'events';
import { useGetProductList } from 'pages/PosPhonesShop/MyParValuesSelect';

export type BankAccountsListModalProps = {
  form: FormInstance;
  refetch: () => void;
  setVisible: (visible: boolean) => void;
  tpid?: number;
  action: string;
  record?: any;
};

// Step 1: Define the props interface for DynamicPrioritySelect
interface DynamicPrioritySelectProps {
  tpid?: number;
  prcode: string;
  excludedTpbkid?: number;
  value?: string | number;
  onChange?: (value: string | number) => void;
  className?: string;
}

const BankAccountModal = (props: BankAccountsListModalProps) => {
  const {
    action,
    record,
    tpid,
    form,
    setVisible,
    refetch,
  } = props;

  const { data: products } = useGetProductList();

  // Step 2: Create the DynamicPrioritySelect component with proper typing
  const DynamicPrioritySelect = ({ 
    tpid, 
    prcode, 
    excludedTpbkid, 
    value, 
    onChange, 
    className 
  }: DynamicPrioritySelectProps) => {
    const [availablePriorities, setAvailablePriorities] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);

    // Step 3: Fetch available priorities when component mounts or props change
    useEffect(() => {
      const fetchAvailablePriorities = async () => {
        if (!tpid || !prcode) return;

        setLoading(true);
        try {
          const response = await getAvailablePriorities({
            tpid,
            prcode,
            excludedTpbkid
          });

          if (response.code === '200' && response.data) {
            setAvailablePriorities(response.data);
          } else {
            // Fallback to default priorities 1-6 if API fails
            setAvailablePriorities([1, 2, 3, 4, 5, 6]);
          }
        } catch (error) {
          console.error('Error fetching available priorities:', error);
          // Fallback to default priorities 1-6 if API fails
          setAvailablePriorities([1, 2, 3, 4, 5, 6]);
        } finally {
          setLoading(false);
        }
      };

      fetchAvailablePriorities();
    }, [tpid, prcode, excludedTpbkid]);

    // Step 4: Return the Select component with available priorities
    return (
      <Select
        className={className}
        value={value}
        onChange={onChange}
        loading={loading}
        placeholder="Sélectionner une priorité"
        style={{ width: '100%' }}
      >
        {availablePriorities.map((priority) => (
          <Select.Option key={priority} value={priority.toString()}>
            {priority}
          </Select.Option>
        ))}
      </Select>
    );
  };

  const onSave = useCallback(
    async (formData: any) => {
      try {
        const tpBankAccountProductList = products
          ?.filter((product) => formData[product.prcode]) 
          .map((product) => ({
            tpid: tpid, 
            tpbkid: record?.tpbkid, 
            prcode: product.prcode,
            priority: parseInt(formData.priority?.[product.prcode] || '1', 10), 
          }));

        const { priority, ...restValues } = formData; 
        products?.forEach((product) => {
          delete restValues[product.prcode]; 
        });

        const finalPayload = {
          ...restValues, 
          tpid: tpid, 
          tpBankAccountProductList, 
        };

        console.log('Payload envoyé au backend :', finalPayload); 

        const response = await updateThirdPartyBkAccount({
          bkrequest: finalPayload, 
          tpbkid: record.tpbkid,
          tpid: tpid!,
        });

        if (response.code === '200') {
          refetch();
          Notifications({
            status: 'success',
            message: 'Modification effectuée avec succès',
            duration: 10,
            placement: 'topRight',
          });
          setVisible(false);
        }
      } catch (error) {
        notification.error({ message: 'Une erreur est survenue lors de enregistrement' });
      }
    },
    [tpid, record, refetch, setVisible, products] 
  );

  // Step 5: Initialize form values based on action (create or update)
  useEffect(() => {
    if (action === 'create') {
      form.resetFields();
      form.setFieldsValue({
        bktype: 'INOUT',
        currcode: 'EUR',
        countcode: 'FR',
        tpcbtype: '',
        bkstatus: 'INIT',
        tpid: tpid,
        bkstartdate: dayjs(),
        bkstatusdate: dayjs()
      });
    } else if (record) {
      // Transform the product list into the correct format for the form
      const formValues = {
        ...record,
        bkstartdate: record.bkstartdate ? dayjs(record.bkstartdate) : null,
        bkenddate: record.bkenddate ? dayjs(record.bkenddate) : null,
        bkstatusdate: record.bkstatusdate ? dayjs(record.bkstatusdate) : null,
      };

      if (record.tpBankAccountProductList) {
        record.tpBankAccountProductList.forEach((product: any) => {
          // Set the checkbox value
          formValues[product.prcode] = true;
          // Set the priority value
          if (!formValues.priority) formValues.priority = {};
          formValues.priority[product.prcode] = product.priority.toString();
        });
      }

      form.setFieldsValue(formValues);
    }
  }, [action, record, form, tpid]);

  const handleSubmit = async () => {
    try {
      if (action !== 'create') return;

      const values = await form.validateFields();

      // Étape 1 : construire tpBankAccountProductList
      const tpBankAccountProductList = products
        ?.filter((product) => values[product.prcode])
        .map((product) => ({
          tpid: tpid,
          tpbkid: undefined,
          prcode: product.prcode,
          priority: parseInt(values.priority?.[product.prcode] || '1', 10),
        }));

      // Étape 2 : cloner et nettoyer les valeurs pour éviter d'envoyer VN/VO/VD/PR
      const cleanedValues = { ...values };
      products?.forEach((product) => {
        delete cleanedValues[product.prcode]; // supprime VN, VD, ...
      });
      delete cleanedValues.priority;

      // Étape 3 : construire le payload
      const payload = {
        ...cleanedValues,
        tpid: tpid,
        tpBankAccountProductList: tpBankAccountProductList || [],
        bktype: cleanedValues.bktype || 'INOUT',
        currcode: cleanedValues.currcode || 'EUR',
        countcode: cleanedValues.countcode || 'FR',
        tpcbtype: cleanedValues.tpcbtype || '',
        bkstatus: cleanedValues.bkstatus || 'ACTIVE',
      };

      console.log("payload envoyé :", payload);

      // Étape 4 : appel backend
      const response = await createThirdPartyBkAccount({
        tpid: tpid!,
        bkrequest: payload
      });

      if (response.code === '200') {
        refetch();
        Notifications({
          status: "success",
          message: "Compte bancaire créé avec succès",
          duration: 6,
          placement: 'topRight',
        });
        setVisible(false);
      } else {
        Notifications({
          status: "error",
          message: response.message || "Erreur lors de la création",
          duration: 6,
          placement: 'topRight',
        });
      }

    } catch (error) {
      console.error('Erreur de validation du formulaire :', error);
      Notifications({
        status: "error",
        message: "Erreur de validation du formulaire",
        duration: 6,
        placement: 'topRight',
      });
    }
  };

  return (
    <Form layout="vertical" form={form}
      onFinish={(values) => {
        const value = {
          ...values,
          tpbkid: record.tpbkid,
        };
        onSave(value);
      }}>

      <StaticCard
        title=""
        value={" Il est recommandé que l'ajout d'un compte soit fait par le concessionnaire."}
        variant="notif"
        className="mb-8 staticNotf mt-8"
        valueStyle={{ backgroundColor: '#F7C0A929' }}
      />
      
      <Bank
        form={form}
        countcode={form.getFieldValue('countcode')}
        currcode={form.getFieldValue('currcode')}
        bkHolder="bkholder"
        ibanField="tpiban"
        bicField="tpbic"
        banknameField="tpbankname"
        action={action}
        className='no-border-radius'
       />

      <Row className='no-border-radius' gutter={12} style={{ marginBottom: 15 }}>
        {products?.map((product) => (
          <React.Fragment key={product.prcode}>
            <Col span={12}>
              <Row gutter={10}>
                <Col span={6}>
                  <Form.Item
                    name={product.prcode}
                    valuePropName="checked"
                  >
                    <Checkbox>{product.prcode}</Checkbox>
                  </Form.Item>
                </Col>
                <Col span={18}>
                  <Form.Item shouldUpdate noStyle>
                    {() =>
                      form.getFieldValue(product.prcode) && (
                        <Form.Item
                          name={['priority', product.prcode]}
                          noStyle
                        >
                          <DynamicPrioritySelect 
                            className="ut-input-modal"
                            tpid={tpid}
                            prcode={product.prcode}
                            excludedTpbkid={action === 'update' ? record?.tpbkid : undefined}
                          />
                        </Form.Item>
                      )
                    }
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </React.Fragment>
        ))}
      </Row>

      <Row justify='end'>
        <ButtonLink comcode="Annuler" onClick={() => setVisible(false)} />
        <ButtonLink type="primary" comcode="Valider" htmlType="submit" className="ml-20" />
      </Row>
    </Form>
  );
};

export default BankAccountModal;